/**
 * PageHeader
 *
 * Standard admin page header. One row, no card chrome — keeps the page
 * itself uncluttered:
 *
 *   [ filled icon tile ]  Title  ‹count chip›        ‹actions slot›
 *                         Subtitle / description
 *
 * Actions slot is where pages drop their right-cluster controls — a
 * search input + primary CTA on Workspaces, just a CTA on Tags, etc.
 *
 * Subtitle remains supported for back-compat; new pages should use the
 * `description` field which reads more naturally next to `actions`.
 */

import React from 'react';
import { Box, Typography } from '../ui';

interface PageHeaderProps {
  icon?: React.ReactNode;
  title: string;
  /** One-line description rendered under the title. */
  subtitle?: string;
  /** Alias for `subtitle` — preferred name on newer call sites. */
  description?: string;
  /** Optional small grey count chip rendered inline with the title. */
  count?: number;
  /** Right-cluster slot — typically a search input + primary CTA. */
  actions?: React.ReactNode;
}

export default function PageHeader({
  icon,
  title,
  subtitle,
  description,
  count,
  actions,
}: PageHeaderProps) {
  const desc = description ?? subtitle;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        {icon && (
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              // Force a predictable icon size — MUI icons inherit CSS
              // `fontSize`, so this propagates into whatever SVG the
              // caller passes in (no need to set `fontSize` per call).
              '& svg': { fontSize: 24 },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            {/* Title inherits h4 from the theme (700 weight, tight
                tracking, dark) — matches the bold dashboard headline
                in the reference design. */}
            <Typography variant="h4">{title}</Typography>
            {typeof count === 'number' && count > 0 && (
              <Typography
                variant="body2"
                color="primary.main"
                fontWeight={600}
                sx={{ lineHeight: 1 }}
              >
                ({count})
              </Typography>
            )}
          </Box>
          {desc && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 0.5, fontWeight: 400 }}
            >
              {desc}
            </Typography>
          )}
        </Box>
      </Box>
      {actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          {actions}
        </Box>
      )}
    </Box>
  );
}
